import clsx from 'clsx'
import _ from 'lodash'
import {
    ClassifierSearchParamsView,
    ContentFeedView,
    useDeleteContentFeedMutation,
    useContentSearchCountQuery
} from '../../api/client'
import { formatCappedCount } from '../../util/searchCounts'
import { toastError } from '../Notification'
import { ContentFeedKPILoadingSkeleton } from './ContentFeedLoadingSkeleton'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { TrashIcon } from '@heroicons/react/24/outline'

type Props = {
    contentFeed: ContentFeedView
    // We need to let the parent component know when we're deleted
    onDeleted: (id: number) => void
}

/**
 * Displays a ContentFeed as a Card
 */
export default function ContentFeedCard({ contentFeed, onDeleted }: Props) {
    // This is a little messy. The searchParams model is from the API and includes the __typename property used by GraphQL
    // When using this as a search parameter for the count this throws an error as __typename is not a valid property to
    // include in the query.
    // To resolve this we clondDeep (which includes nested objects) and remove the __typename property from the root object
    // and also from the child objects, in this case just the classifier definitions.
    const params = _.cloneDeep(contentFeed.searchParameters)
    delete params.__typename

    if (params.classifierSearchParams && Array.isArray(params.classifierSearchParams)) {
        params.classifierSearchParams.forEach((param: ClassifierSearchParamsView) => {
            if (param && typeof param === 'object' && param.__typename) {
                delete param.__typename
            }
        })
    }

    const { data: countData, loading: countLoading } = useContentSearchCountQuery({
        fetchPolicy: 'no-cache',
        variables: {
            params: { ...params, authorProfileIds: [] }
        },
        onError: (error) => {
            console.error("Couldn't fetch content count", error)
            toastError('There was an error fetching feed metrics.')
        }
    })

    const [deleteContentFeedMutation] = useDeleteContentFeedMutation({
        onError: (error) => {
            toastError(error.message)
        }
    })

    // State to track which dashboard is being deleted
    const [deletingContentFeedId, setDeletingContentFeedId] = useState<number | null>(null)

    const handleDelete = async (contentFeedId: number) => {
        setDeletingContentFeedId(contentFeedId)
        try {
            await deleteContentFeedMutation({
                variables: { contentFeedId }
            })
            onDeleted(contentFeedId)
        } finally {
            setDeletingContentFeedId(null)
        }
    }

    const total = countData?.contentSearchCount || 0
    // We use a link rather than an onClick handler + navigate because a link allows
    // our users to 'open in a new tab'.
    return (
        <>
            <Link
                key={contentFeed.id}
                to={`content-search/feed/${contentFeed.id}`}
                className="w-52"
            >
                {/* Compact stat tile — the label carries the delete affordance so the
                    count can sit alone as the tile's one big number. */}
                <div
                    className={clsx(
                        'bg-white cursor-pointer no-underline',
                        'px-3.5 py-3',
                        'block border border-ink-200 rounded-[10px] shadow-sm transition-shadow',
                        'hover:shadow-md'
                    )}
                >
                    <dl>
                        <dt className="flex items-center justify-between gap-2 text-xs text-ink-500">
                            <span className="truncate">{contentFeed.displayName}</span>
                            {deletingContentFeedId === contentFeed.id ? (
                                <div className="h-4 w-4 flex-none animate-spin rounded-full border-2 border-ink-300 border-t-arwen-600"></div>
                            ) : (
                                <button
                                    type="button"
                                    aria-label={`Delete feed ${contentFeed.displayName}`}
                                    className="flex-none text-ink-400 hover:text-arwen-600"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                        handleDelete(contentFeed.id)
                                    }}
                                >
                                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                </button>
                            )}
                        </dt>
                        <dd className="mt-0.5 text-2xl font-semibold leading-tight text-ink-900">
                            {countLoading ? <ContentFeedKPILoadingSkeleton /> : <>{formatCappedCount(total)}</>}
                        </dd>
                    </dl>
                </div>
            </Link>
        </>
    )
}
